import React, { useState } from 'react';
import api from '../utils/axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Search,
  RefreshCw,
  ExternalLink,
  Building2,
  User,
  MapPin,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';

const ESTADO_CONFIG = {
  ACTIVO: { 
    icon: CheckCircle, 
    color: 'text-green-600', 
    bg: 'bg-green-50 border-green-200',
    badge: 'bg-green-100 text-green-700'
  },
  LIMITADO: { 
    icon: AlertTriangle, 
    color: 'text-amber-600', 
    bg: 'bg-amber-50 border-amber-200',
    badge: 'bg-amber-100 text-amber-700'
  },
  INACTIVO: { 
    icon: XCircle, 
    color: 'text-red-600', 
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700'
  },
  NO_ENCONTRADO: { 
    icon: XCircle, 
    color: 'text-slate-600', 
    bg: 'bg-slate-50 border-slate-200',
    badge: 'bg-slate-100 text-slate-700'
  },
};

/**
 * Componente para verificar un CUIT en AFIP
 * 
 * @param {string} cuit - CUIT a verificar
 * @param {function} onVerified - Callback cuando se verifica exitosamente
 * @param {boolean} showDetails - Mostrar información detallada
 * @param {string} variant - 'button' | 'inline' | 'card'
 */
export default function AFIPVerification({ 
  cuit, 
  onVerified, 
  showDetails = false,
  variant = 'button',
  className = ''
}) {
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  const verificarCuit = async () => {
    if (!cuit || cuit.replace(/-/g, '').length !== 11) {
      toast.error('Ingrese un CUIT válido (11 dígitos)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = showDetails ? `/afip/consultar/${cuit}` : `/afip/verificar/${cuit}`;
      const res = await api.get(endpoint);
      
      setResultado(res.data);
      
      if (res.data.success) {
        if (res.data.estado === 'ACTIVO') {
          toast.success(`CUIT verificado: ${res.data.nombre}`);
        } else if (res.data.estado === 'LIMITADO') {
          toast.warning(`CUIT con estado limitado: ${res.data.nombre}`);
        } else {
          toast.error(`CUIT inactivo: ${res.data.nombre || 'Sin información'}`);
        }
        
        if (onVerified) {
          onVerified(res.data);
        }
      } else {
        setError(res.data.message || 'Error al verificar CUIT');
        toast.error(res.data.message || 'Error al verificar CUIT');
      }
    } catch (err) {
      const mensaje = err.response?.data?.detail || 'Error al conectar con AFIP';
      setError(mensaje);
      toast.error(mensaje);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoConfig = (estado) => {
    return ESTADO_CONFIG[estado] || ESTADO_CONFIG.NO_ENCONTRADO;
  };

  // Variante: Solo botón
  if (variant === 'button') {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={verificarCuit}
        disabled={loading || !cuit}
        className={`text-blue-600 border-blue-300 hover:bg-blue-50 ${className}`}
      >
        {loading ? (
          <RefreshCw size={14} className="mr-2 animate-spin" />
        ) : (
          <Search size={14} className="mr-2" />
        )}
        Verificar AFIP
      </Button>
    );
  }

  // Variante: Inline (botón + resultado en línea)
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={verificarCuit}
          disabled={loading || !cuit}
          className="text-blue-600 border-blue-300 hover:bg-blue-50"
        >
          {loading ? (
            <RefreshCw size={14} className="mr-2 animate-spin" />
          ) : (
            <Search size={14} className="mr-2" />
          )}
          Verificar AFIP
        </Button>
        
        {resultado && resultado.success && (
          <Badge className={getEstadoConfig(resultado.estado).badge}>
            {React.createElement(getEstadoConfig(resultado.estado).icon, { size: 12, className: 'mr-1' })}
            {resultado.estado}
          </Badge>
        )}
        
        {error && (
          <span className="text-xs text-red-600">{error}</span>
        )}
      </div>
    );
  }

  // Variante: Card (información completa)
  if (variant === 'card') {
    const config = resultado ? getEstadoConfig(resultado.estado) : null;
    
    return (
      <div className={className}>
        {/* Botón de verificación */}
        {!resultado && (
          <Button
            type="button"
            variant="outline"
            onClick={verificarCuit}
            disabled={loading || !cuit}
            className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
          >
            {loading ? (
              <RefreshCw size={16} className="mr-2 animate-spin" />
            ) : (
              <Search size={16} className="mr-2" />
            )}
            Verificar estado en AFIP
          </Button>
        )}

        {/* Error */}
        {error && !resultado && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600 flex items-center gap-2">
              <XCircle size={16} />
              {error}
            </p>
          </div>
        )}

        {/* Resultado */}
        {resultado && resultado.success && (
          <div className={`p-4 border rounded-lg ${config.bg}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {React.createElement(config.icon, { size: 20, className: config.color })}
                <div>
                  <p className="font-semibold text-slate-900">{resultado.nombre}</p>
                  <p className="text-xs text-slate-500 font-mono">{resultado.cuit}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={config.badge}>
                  {resultado.estado_label || resultado.estado}
                </Badge>
                {resultado.modo_demo && (
                  <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                    MODO DEMO
                  </span>
                )}
              </div>
            </div>

            {/* Detalles adicionales */}
            {showDetails && (
              <div className="space-y-2 mt-3 pt-3 border-t border-slate-200">
                {resultado.tipo_persona && (
                  <div className="flex items-center gap-2 text-sm">
                    {resultado.tipo_persona === 'JURIDICA' ? (
                      <Building2 size={14} className="text-slate-400" />
                    ) : (
                      <User size={14} className="text-slate-400" />
                    )}
                    <span className="text-slate-600">
                      {resultado.tipo_persona === 'JURIDICA' ? 'Persona Jurídica' : 'Persona Física'}
                    </span>
                  </div>
                )}
                
                {resultado.domicilio_fiscal && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin size={14} className="text-slate-400 mt-0.5" />
                    <span className="text-slate-600">{resultado.domicilio_fiscal}</span>
                  </div>
                )}
                
                {resultado.actividad_principal && (
                  <div className="flex items-start gap-2 text-sm">
                    <Briefcase size={14} className="text-slate-400 mt-0.5" />
                    <span className="text-slate-600">{resultado.actividad_principal}</span>
                  </div>
                )}
                
                {resultado.categoria_monotributo && (
                  <div className="text-sm">
                    <Badge variant="outline" className="text-xs">
                      Monotributo: {resultado.categoria_monotributo}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Botón para re-verificar */}
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-400">
                Verificado: {new Date(resultado.fecha_consulta).toLocaleString('es-AR')}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={verificarCuit}
                disabled={loading}
                className="text-slate-500 hover:text-slate-700"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        )}

        {/* CUIT no encontrado */}
        {resultado && !resultado.success && (
          <div className="p-4 border rounded-lg bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 text-slate-600">
              <XCircle size={20} />
              <div>
                <p className="font-medium">{resultado.error}</p>
                <p className="text-sm text-slate-500">{resultado.message}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={verificarCuit}
              disabled={loading}
              className="mt-2 text-slate-500"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Reintentar
            </Button>
          </div>
        )}
      </div>
    );
  }

  return null;
}
